export const translations = {
  en: {
    nav: {
      solutions: 'Solutions',
      industries: 'Industries',
      technologies: 'Technologies',
      references: 'References',
      blog: 'Blog',
      contact: 'Contact'
    },
    hero: {
      title: 'IoT Solutions That Reduce Operating Costs Across the Baltics',
      subtitle: 'Trusted by municipalities, industries, airports and public institutions in Latvia, Lithuania and Estonia.',
      benefits: [
        'Smart metering',
        'Energy monitoring',
        'Asset tracking',
        'Indoor air quality',
        'Libraries & Parking AI'
      ],
      cta1: 'Book a consultation',
      cta2: 'Download overview'
    },
    trust: {
      title: 'Trusted by Leading Organizations Across the Baltics'
    },
    solutions: {
      title: 'What We Do',
      subtitle: 'Comprehensive IoT solutions for modern businesses and institutions',
      items: [
        {
          title: 'Smart Water & Gas Metering',
          description: 'Automated meter reading and consumption monitoring for utilities. Reduce manual readings, detect leaks early, and optimize resource distribution.',
          benefit: 'Up to 30% reduction in water loss'
        },
        {
          title: 'Energy Monitoring & HVAC Analytics',
          description: 'Real-time energy consumption tracking and HVAC optimization. Identify inefficiencies, reduce costs, and meet sustainability goals.',
          benefit: 'Average 25% energy cost savings'
        },
        {
          title: 'Asset & Trolley Tracking',
          description: 'GPS and BLE-based tracking for assets, trolleys, and equipment in airports, warehouses, and facilities. Never lose track of valuable assets.',
          benefit: '95% asset utilization improvement'
        },
        {
          title: 'Indoor Air Quality Monitoring',
          description: 'CO2, humidity, temperature, and VOC monitoring for healthy indoor environments. Ensure compliance and improve occupant well-being.',
          benefit: '40% better air quality compliance'
        },
        {
          title: 'Smart Libraries & Automated Lockers',
          description: 'RFID-based library management and automated locker systems. Streamline operations, reduce staff workload, and enhance user experience.',
          benefit: '60% operational efficiency gain'
        },
        {
          title: 'Parking & Flow AI (Viziosense)',
          description: 'Computer vision-based parking management and flow analysis. Optimize space utilization, reduce congestion, and improve customer satisfaction.',
          benefit: '35% parking revenue increase'
        },
        {
          title: 'E2-Care for Elderly Care',
          description: 'Remote monitoring and assistance solutions for elderly care facilities. Track health metrics, ensure safety, and improve quality of life for seniors.',
          benefit: '50% faster emergency response time'
        },
        {
          title: 'E2-Health for Healthcare Digitalization',
          description: 'Complete digital health infrastructure for hospitals and clinics. Patient monitoring, asset tracking, and environmental compliance in one platform.',
          benefit: '40% improvement in patient care efficiency'
        }
      ],
      cta: 'Learn more'
    },
    industries: {
      title: 'Industries We Serve',
      subtitle: 'Specialized solutions tailored to your sector',
      items: [
        {
          title: 'Municipalities & Utilities',
          description: 'Smart metering, leak detection, and infrastructure monitoring for water, gas, and district heating networks.'
        },
        {
          title: 'Industry & Manufacturing',
          description: 'Energy optimization, asset tracking, and environmental monitoring for production facilities.'
        },
        {
          title: 'Logistics & Warehouses',
          description: 'Asset tracking, environmental control, and efficiency analytics for modern supply chains.'
        },
        {
          title: 'Airports & Critical Infrastructure',
          description: 'Trolley tracking, flow monitoring, and facility management for aviation and transport hubs.'
        },
        {
          title: 'Libraries & Public Institutions',
          description: 'RFID automation, access control, and locker management for public services.'
        },
        {
          title: 'Healthcare Providers',
          description: 'Air quality monitoring, asset tracking, and environmental compliance for medical facilities.'
        },
        {
          title: 'Parking Operators & Smart Cities',
          description: 'AI-powered parking management and urban flow analytics for modern cities.'
        },
        {
          title: 'Real Estate & Facility Management',
          description: 'Energy monitoring, access control, and building automation for commercial properties.'
        }
      ]
    },
    technologies: {
      title: 'Technologies We Use',
      e2: {
        title: 'E2 Technologies',
        description: 'Our proprietary IoT platform combining advanced sensors, connectivity, and cloud analytics for seamless data collection and monitoring.',
        features: [
          'Multi-network connectivity (Sigfox, LoRa, NB-IoT, LTE-M)',
          'Repeater infrastructure for extended coverage',
          'Easy device onboarding via mobile app',
          'Real-time data visualization and alerts'
        ],
        cta: 'Explore E2'
      },
      viziosense: {
        title: 'Viziosense AI',
        description: 'Computer vision and AI-powered analytics for parking management and flow monitoring, delivering actionable insights without privacy concerns.',
        features: [
          'Privacy-first anonymous detection',
          'Real-time occupancy monitoring',
          'Flow pattern analysis and predictions',
          'Integration with existing systems'
        ],
        cta: 'Discover Viziosense'
      },
      connectivity: {
        title: 'Connectivity Options',
        description: 'Multiple wireless technologies to match your specific needs and infrastructure',
        networks: [
          {
            name: 'Sigfox',
            description: 'Ultra-low power, long-range connectivity for simple sensor networks'
          },
          {
            name: 'LoRa',
            description: 'Private network capability with excellent building penetration'
          },
          {
            name: 'NB-IoT',
            description: 'Cellular IoT with deep coverage and high reliability'
          },
          {
            name: 'LTE-M',
            description: 'Mobile IoT for applications requiring higher data rates'
          },
          {
            name: 'BLE',
            description: 'Bluetooth Low Energy for short-range asset tracking'
          },
          {
            name: 'wMBUS',
            description: 'Wireless M-Bus for utility metering applications'
          },
          {
            name: 'Mioty',
            description: 'Massive IoT connectivity for dense sensor deployments'
          }
        ]
      },
      rfid: {
        title: 'RFID & Access Control',
        description: 'Secure identification and automation systems for libraries, lockers, and facilities.',
        features: [
          'HF and UHF RFID technology',
          'Automated gate and locker systems',
          'User authentication and logging',
          'Integration with existing library systems'
        ],
        cta: 'View RFID solutions'
      }
    },
    cases: {
      title: 'Success Stories',
      subtitle: 'Real results from our Baltic clients',
      items: [
        {
          title: 'Riga Water Utility',
          problem: 'Manual meter readings were costly and prone to errors, with significant water losses due to undetected leaks.',
          solution: 'Deployed 15,000 smart water meters with automated reading and leak detection across the city.',
          roi: '€450,000 annual savings through reduced labor costs and 28% reduction in water losses',
          cta: 'Read full case'
        },
        {
          title: 'Vilnius Airport',
          problem: 'Lost and misplaced luggage trolleys resulted in poor passenger experience and high replacement costs.',
          solution: 'Implemented BLE-based trolley tracking system covering all terminals and parking areas.',
          roi: '92% trolley utilization rate, €120,000 savings on trolley replacements annually',
          cta: 'Read full case'
        },
        {
          title: 'Tallinn Central Library',
          problem: 'Manual book check-in/out processes were slow, leading to long queues and limited self-service options.',
          solution: 'Installed RFID-based automated gates and self-service stations for 180,000 books.',
          roi: '65% faster processing, 40% reduction in staff workload, 95% user satisfaction',
          cta: 'Read full case'
        }
      ]
    },
    regional: {
      title: 'Local Presence Across the Baltics',
      subtitle: 'Expert teams ready to serve you',
      offices: [
        {
          city: 'Vilnius',
          country: 'Lithuania',
          description: 'Our Lithuania headquarters with technical support and project management'
        },
        {
          city: 'Riga',
          country: 'Latvia',
          description: 'Full-service office serving Latvian municipalities and enterprises'
        },
        {
          city: 'Tallinn',
          country: 'Estonia',
          description: 'Estonian operations center for local project delivery'
        }
      ]
    },
    contact: {
      title: 'Get Started Today',
      subtitle: 'Let us discuss how IoT can transform your operations',
      form: {
        fullName: 'Full Name',
        company: 'Company Name',
        email: 'Email Address',
        phone: 'Phone Number (optional)',
        country: 'Country',
        projectType: 'Project Type',
        numSites: 'Number of Sites / Assets',
        urgency: 'Project Urgency',
        message: 'Tell us about your needs',
        submit: 'Get Started',
        countries: ['Lithuania', 'Latvia', 'Estonia', 'Other'],
        projectTypes: [
          'Smart Metering',
          'Energy Monitoring',
          'Asset Tracking',
          'Indoor Air Quality',
          'Smart Libraries',
          'Parking Management',
          'Other'
        ],
        siteOptions: ['1-10', '11-50', '51-200', '200+'],
        urgencyOptions: ['Urgent (< 1 month)', 'Soon (1-3 months)', 'Planning (3-6 months)', 'Future (6+ months)']
      }
    },
    blog: {
      title: 'Latest Insights',
      subtitle: 'IoT trends, case studies, and industry news',
      readMore: 'Read more'
    },
    footer: {
      solutions: 'Solutions',
      company: 'Company',
      legal: 'Legal',
      offices: 'Our Offices',
      lithuania: 'Lithuania',
      latvia: 'Latvia',
      estonia: 'Estonia',
      rights: '© 2025 0g Baltics — All rights reserved.'
    }
  },
  de: {
    nav: {
      solutions: 'Lösungen',
      industries: 'Branchen',
      technologies: 'Technologien',
      references: 'Referenzen',
      blog: 'Blog',
      contact: 'Kontakt'
    },
    hero: {
      title: 'IoT-Lösungen zur Senkung der Betriebskosten im Baltikum',
      subtitle: 'Vertrauen von Gemeinden, Industrie, Flughäfen und öffentlichen Einrichtungen in Lettland, Litauen und Estland.',
      benefits: [
        'Smart Metering',
        'Energieüberwachung',
        'Asset-Tracking',
        'Raumluftqualität',
        'Bibliotheken & Parking-KI'
      ],
      cta1: 'Beratung buchen',
      cta2: 'Übersicht herunterladen'
    },
    trust: {
      title: 'Vertraut von führenden Organisationen im Baltikum'
    },
    solutions: {
      title: 'Was wir tun',
      subtitle: 'Umfassende IoT-Lösungen für moderne Unternehmen und Institutionen',
      items: [
        {
          title: 'Smart Wasser- & Gaszähler',
          description: 'Automatische Zählerablesung und Verbrauchsüberwachung für Versorgungsunternehmen. Reduzieren Sie manuelle Ablesungen, erkennen Sie Lecks frühzeitig.',
          benefit: 'Bis zu 30% Reduzierung des Wasserverlusts'
        },
        {
          title: 'Energieüberwachung & HVAC-Analyse',
          description: 'Echtzeit-Energieverbrauchserfassung und HVAC-Optimierung. Identifizieren Sie Ineffizienzen und senken Sie Kosten.',
          benefit: 'Durchschnittlich 25% Energiekosteneinsparung'
        },
        {
          title: 'Asset & Trolley-Tracking',
          description: 'GPS- und BLE-basierte Verfolgung von Assets, Trolleys und Ausrüstung in Flughäfen, Lagerhäusern und Anlagen.',
          benefit: '95% Verbesserung der Asset-Auslastung'
        },
        {
          title: 'Raumluftqualitäts-Monitoring',
          description: 'CO2-, Feuchtigkeits-, Temperatur- und VOC-Überwachung für gesunde Innenräume.',
          benefit: '40% bessere Luftqualitäts-Compliance'
        },
        {
          title: 'Smart Libraries & Automatische Schließfächer',
          description: 'RFID-basiertes Bibliotheksmanagement und automatisierte Schließfachsysteme. Optimieren Sie Abläufe und Nutzererlebnis.',
          benefit: '60% Effizienzgewinn im Betrieb'
        },
        {
          title: 'Parking & Flow KI (Viziosense)',
          description: 'Computer-Vision-basiertes Parkraummanagement und Flussanalyse. Optimieren Sie Raumnutzung und Kundenzufriedenheit.',
          benefit: '35% Umsatzsteigerung beim Parken'
        }
      ],
      cta: 'Mehr erfahren'
    },
    industries: {
      title: 'Branchen, die wir bedienen',
      subtitle: 'Spezialisierte Lösungen für Ihren Sektor',
      items: [
        {
          title: 'Gemeinden & Versorgung',
          description: 'Smart Metering, Leckageerkennung und Infrastrukturüberwachung für Wasser-, Gas- und Fernwärmenetze.'
        },
        {
          title: 'Industrie & Fertigung',
          description: 'Energieoptimierung, Asset-Tracking und Umweltüberwachung für Produktionsanlagen.'
        },
        {
          title: 'Logistik & Lagerhäuser',
          description: 'Asset-Tracking, Umgebungskontrolle und Effizienzanalysen für moderne Lieferketten.'
        },
        {
          title: 'Flughäfen & Kritische Infrastruktur',
          description: 'Trolley-Tracking, Flussüberwachung und Facility-Management für Luftfahrt- und Verkehrsknotenpunkte.'
        },
        {
          title: 'Bibliotheken & Öffentliche Einrichtungen',
          description: 'RFID-Automatisierung, Zutrittskontrolle und Schließfachverwaltung für öffentliche Dienste.'
        },
        {
          title: 'Gesundheitsdienstleister',
          description: 'Luftqualitätsüberwachung, Asset-Tracking und Umwelt-Compliance für medizinische Einrichtungen.'
        },
        {
          title: 'Parkbetreiber & Smart Cities',
          description: 'KI-gestütztes Parkraummanagement und urbane Flussanalysen für moderne Städte.'
        },
        {
          title: 'Immobilien & Facility Management',
          description: 'Energieüberwachung, Zutrittskontrolle und Gebäudeautomation für Gewerbeimmobilien.'
        }
      ]
    },
    technologies: {
      title: 'Technologien, die wir einsetzen',
      e2: {
        title: 'E2 Technologies',
        description: 'Unsere proprietäre IoT-Plattform kombiniert fortschrittliche Sensoren, Konnektivität und Cloud-Analysen.',
        features: [
          'Multi-Netzwerk-Konnektivität (Sigfox, LoRa, NB-IoT, LTE-M)',
          'Repeater-Infrastruktur für erweiterte Abdeckung',
          'Einfaches Geräte-Onboarding über mobile App',
          'Echtzeit-Datenvisualisierung und Alarme'
        ],
        cta: 'E2 erkunden'
      },
      viziosense: {
        title: 'Viziosense KI',
        description: 'Computer Vision und KI-gestützte Analysen für Parkraummanagement und Flussüberwachung.',
        features: [
          'Datenschutzfreundliche anonyme Erkennung',
          'Echtzeit-Belegungsüberwachung',
          'Flussmusteranalyse und Vorhersagen',
          'Integration in bestehende Systeme'
        ],
        cta: 'Viziosense entdecken'
      },
      connectivity: {
        title: 'Konnektivitätsoptionen',
        description: 'Verschiedene Funktechnologien passend zu Ihren Anforderungen',
        networks: [
          {
            name: 'Sigfox',
            description: 'Ultra-Low-Power, Langstrecken-Konnektivität für einfache Sensornetzwerke'
          },
          {
            name: 'LoRa',
            description: 'Private Netzwerkfähigkeit mit exzellenter Gebäudedurchdringung'
          },
          {
            name: 'NB-IoT',
            description: 'Cellular IoT mit tiefer Abdeckung und hoher Zuverlässigkeit'
          },
          {
            name: 'LTE-M',
            description: 'Mobiles IoT für Anwendungen mit höheren Datenraten'
          },
          {
            name: 'BLE',
            description: 'Bluetooth Low Energy für kurzreichweitiges Asset-Tracking'
          },
          {
            name: 'wMBUS',
            description: 'Wireless M-Bus für Versorgungszähleranwendungen'
          },
          {
            name: 'Mioty',
            description: 'Massive IoT-Konnektivität für dichte Sensoreinsätze'
          }
        ]
      },
      rfid: {
        title: 'RFID & Zutrittskontrolle',
        description: 'Sichere Identifikations- und Automatisierungssysteme für Bibliotheken, Schließfächer und Anlagen.',
        features: [
          'HF- und UHF-RFID-Technologie',
          'Automatisierte Tor- und Schließfachsysteme',
          'Benutzerauthentifizierung und Protokollierung',
          'Integration in bestehende Bibliothekssysteme'
        ],
        cta: 'RFID-Lösungen ansehen'
      }
    },
    cases: {
      title: 'Erfolgsgeschichten',
      subtitle: 'Echte Ergebnisse von unseren baltischen Kunden',
      items: [
        {
          title: 'Rigaer Wasserversorgung',
          problem: 'Manuelle Zählerablesungen waren kostspielig und fehleranfällig, mit erheblichen Wasserverlusten.',
          solution: '15.000 intelligente Wasserzähler mit automatischer Ablesung und Leckageerkennung in der Stadt installiert.',
          roi: '€450.000 jährliche Einsparungen durch reduzierte Arbeitskosten und 28% Reduzierung der Wasserverluste',
          cta: 'Vollständigen Fall lesen'
        },
        {
          title: 'Flughafen Vilnius',
          problem: 'Verlorene und verlegte Gepäckwagen führten zu schlechter Passagiererfahrung und hohen Ersatzkosten.',
          solution: 'BLE-basiertes Trolley-Tracking-System implementiert, das alle Terminals und Parkbereiche abdeckt.',
          roi: '92% Trolley-Auslastungsrate, €120.000 Einsparungen bei Trolley-Ersatz jährlich',
          cta: 'Vollständigen Fall lesen'
        },
        {
          title: 'Tallinner Zentralbibliothek',
          problem: 'Manuelle Buch-Ein-/Ausleihe-Prozesse waren langsam und führten zu langen Warteschlangen.',
          solution: 'RFID-basierte automatisierte Gates und Self-Service-Stationen für 180.000 Bücher installiert.',
          roi: '65% schnellere Verarbeitung, 40% Reduzierung der Mitarbeiterbelastung, 95% Benutzerzufriedenheit',
          cta: 'Vollständigen Fall lesen'
        }
      ]
    },
    regional: {
      title: 'Lokale Präsenz im gesamten Baltikum',
      subtitle: 'Expertenteams bereit, Sie zu bedienen',
      offices: [
        {
          city: 'Vilnius',
          country: 'Litauen',
          description: 'Unser litauisches Hauptquartier mit technischem Support und Projektmanagement'
        },
        {
          city: 'Riga',
          country: 'Lettland',
          description: 'Full-Service-Büro für lettische Gemeinden und Unternehmen'
        },
        {
          city: 'Tallinn',
          country: 'Estland',
          description: 'Estnisches Betriebszentrum für lokale Projektdurchführung'
        }
      ]
    },
    contact: {
      title: 'Heute loslegen',
      subtitle: 'Lassen Sie uns besprechen, wie IoT Ihre Abläufe transformieren kann',
      form: {
        fullName: 'Vollständiger Name',
        company: 'Firmenname',
        email: 'E-Mail-Adresse',
        phone: 'Telefonnummer (optional)',
        country: 'Land',
        projectType: 'Projekttyp',
        numSites: 'Anzahl der Standorte / Assets',
        urgency: 'Projektdringlichkeit',
        message: 'Erzählen Sie uns von Ihren Anforderungen',
        submit: 'Jetzt starten',
        countries: ['Litauen', 'Lettland', 'Estland', 'Andere'],
        projectTypes: [
          'Smart Metering',
          'Energieüberwachung',
          'Asset-Tracking',
          'Raumluftqualität',
          'Smart Libraries',
          'Parkraummanagement',
          'Andere'
        ],
        siteOptions: ['1-10', '11-50', '51-200', '200+'],
        urgencyOptions: ['Dringend (< 1 Monat)', 'Bald (1-3 Monate)', 'Planung (3-6 Monate)', 'Zukunft (6+ Monate)']
      }
    },
    blog: {
      title: 'Neueste Einblicke',
      subtitle: 'IoT-Trends, Fallstudien und Branchennachrichten',
      readMore: 'Mehr lesen'
    },
    footer: {
      solutions: 'Lösungen',
      company: 'Unternehmen',
      legal: 'Rechtliches',
      offices: 'Unsere Büros',
      lithuania: 'Litauen',
      latvia: 'Lettland',
      estonia: 'Estland',
      rights: '© 2025 0g Baltics — Alle Rechte vorbehalten.'
    }
  },
  fr: {
    nav: {
      solutions: 'Solutions',
      industries: 'Industries',
      technologies: 'Technologies',
      references: 'Références',
      blog: 'Blog',
      contact: 'Contact'
    },
    hero: {
      title: 'Solutions IoT qui réduisent les coûts opérationnels dans les pays baltes',
      subtitle: 'Approuvé par les municipalités, industries, aéroports et institutions publiques en Lettonie, Lituanie et Estonie.',
      benefits: [
        'Comptage intelligent',
        'Suivi énergétique',
        'Suivi d\'actifs',
        'Qualité de l\'air intérieur',
        'Bibliothèques & IA parking'
      ],
      cta1: 'Réserver une consultation',
      cta2: 'Télécharger l\'aperçu'
    },
    trust: {
      title: 'Approuvé par les organisations leaders des pays baltes'
    },
    solutions: {
      title: 'Ce que nous faisons',
      subtitle: 'Solutions IoT complètes pour les entreprises et institutions modernes',
      items: [
        {
          title: 'Comptage intelligent eau & gaz',
          description: 'Relevé automatisé des compteurs et surveillance de la consommation pour les services publics. Réduisez les relevés manuels et détectez les fuites.',
          benefit: 'Jusqu\'à 30% de réduction des pertes d\'eau'
        },
        {
          title: 'Suivi énergétique & analytique CVC',
          description: 'Suivi en temps réel de la consommation énergétique et optimisation CVC. Identifiez les inefficacités et réduisez les coûts.',
          benefit: 'En moyenne 25% d\'économies énergétiques'
        },
        {
          title: 'Suivi d\'actifs & chariots',
          description: 'Suivi GPS et BLE pour actifs, chariots et équipements dans aéroports, entrepôts et installations.',
          benefit: '95% d\'amélioration de l\'utilisation des actifs'
        },
        {
          title: 'Suivi de la qualité de l\'air intérieur',
          description: 'Surveillance CO2, humidité, température et COV pour des environnements intérieurs sains.',
          benefit: '40% de meilleure conformité air'
        },
        {
          title: 'Bibliothèques intelligentes & casiers automatisés',
          description: 'Gestion de bibliothèque RFID et systèmes de casiers automatisés. Rationalisez les opérations et l\'expérience utilisateur.',
          benefit: '60% de gain d\'efficacité opérationnelle'
        },
        {
          title: 'Parking & flux IA (Viziosense)',
          description: 'Gestion de parking par vision par ordinateur et analyse de flux. Optimisez l\'utilisation de l\'espace et la satisfaction client.',
          benefit: '35% d\'augmentation des revenus parking'
        }
      ],
      cta: 'En savoir plus'
    },
    industries: {
      title: 'Industries que nous servons',
      subtitle: 'Solutions spécialisées adaptées à votre secteur',
      items: [
        {
          title: 'Municipalités & services publics',
          description: 'Comptage intelligent, détection de fuites et surveillance d\'infrastructure pour réseaux eau, gaz et chauffage urbain.'
        },
        {
          title: 'Industrie & fabrication',
          description: 'Optimisation énergétique, suivi d\'actifs et surveillance environnementale pour installations de production.'
        },
        {
          title: 'Logistique & entrepôts',
          description: 'Suivi d\'actifs, contrôle environnemental et analytique d\'efficacité pour chaînes d\'approvisionnement modernes.'
        },
        {
          title: 'Aéroports & infrastructures critiques',
          description: 'Suivi de chariots, surveillance de flux et gestion d\'installations pour hubs aériens et de transport.'
        },
        {
          title: 'Bibliothèques & institutions publiques',
          description: 'Automatisation RFID, contrôle d\'accès et gestion de casiers pour services publics.'
        },
        {
          title: 'Prestataires de santé',
          description: 'Surveillance qualité de l\'air, suivi d\'actifs et conformité environnementale pour installations médicales.'
        },
        {
          title: 'Opérateurs de parking & villes intelligentes',
          description: 'Gestion de parking alimentée par IA et analytique de flux urbain pour villes modernes.'
        },
        {
          title: 'Immobilier & gestion d\'installations',
          description: 'Surveillance énergétique, contrôle d\'accès et automatisation de bâtiments pour propriétés commerciales.'
        }
      ]
    },
    technologies: {
      title: 'Technologies que nous utilisons',
      e2: {
        title: 'E2 Technologies',
        description: 'Notre plateforme IoT propriétaire combinant capteurs avancés, connectivité et analytique cloud.',
        features: [
          'Connectivité multi-réseau (Sigfox, LoRa, NB-IoT, LTE-M)',
          'Infrastructure de répéteurs pour couverture étendue',
          'Intégration facile des appareils via application mobile',
          'Visualisation de données et alertes en temps réel'
        ],
        cta: 'Explorer E2'
      },
      viziosense: {
        title: 'Viziosense IA',
        description: 'Vision par ordinateur et analytique alimentée par IA pour gestion de parking et surveillance de flux.',
        features: [
          'Détection anonyme respectueuse de la vie privée',
          'Surveillance d\'occupation en temps réel',
          'Analyse et prédictions de modèles de flux',
          'Intégration avec systèmes existants'
        ],
        cta: 'Découvrir Viziosense'
      },
      connectivity: {
        title: 'Options de connectivité',
        description: 'Plusieurs technologies sans fil adaptées à vos besoins spécifiques',
        networks: [
          {
            name: 'Sigfox',
            description: 'Connectivité ultra-basse consommation, longue portée pour réseaux de capteurs simples'
          },
          {
            name: 'LoRa',
            description: 'Capacité de réseau privé avec excellente pénétration des bâtiments'
          },
          {
            name: 'NB-IoT',
            description: 'IoT cellulaire avec couverture profonde et haute fiabilité'
          },
          {
            name: 'LTE-M',
            description: 'IoT mobile pour applications nécessitant des débits de données plus élevés'
          },
          {
            name: 'BLE',
            description: 'Bluetooth Low Energy pour suivi d\'actifs à courte portée'
          },
          {
            name: 'wMBUS',
            description: 'M-Bus sans fil pour applications de comptage de services publics'
          },
          {
            name: 'Mioty',
            description: 'Connectivité IoT massive pour déploiements de capteurs denses'
          }
        ]
      },
      rfid: {
        title: 'RFID & contrôle d\'accès',
        description: 'Systèmes d\'identification sécurisés et d\'automatisation pour bibliothèques, casiers et installations.',
        features: [
          'Technologie RFID HF et UHF',
          'Systèmes de portails et casiers automatisés',
          'Authentification et journalisation des utilisateurs',
          'Intégration avec systèmes de bibliothèque existants'
        ],
        cta: 'Voir solutions RFID'
      }
    },
    cases: {
      title: 'Histoires de réussite',
      subtitle: 'Résultats réels de nos clients baltes',
      items: [
        {
          title: 'Service des eaux de Riga',
          problem: 'Les relevés manuels des compteurs étaient coûteux et sujets aux erreurs, avec pertes d\'eau importantes.',
          solution: 'Déploiement de 15 000 compteurs d\'eau intelligents avec relevé automatique et détection de fuites dans la ville.',
          roi: '€450 000 d\'économies annuelles grâce à la réduction des coûts de main-d\'œuvre et 28% de réduction des pertes d\'eau',
          cta: 'Lire le cas complet'
        },
        {
          title: 'Aéroport de Vilnius',
          problem: 'Les chariots à bagages perdus et égarés entraînaient une mauvaise expérience passagers et coûts de remplacement élevés.',
          solution: 'Système de suivi de chariots BLE implémenté couvrant tous les terminaux et zones de stationnement.',
          roi: '92% de taux d\'utilisation des chariots, €120 000 d\'économies sur remplacements de chariots annuellement',
          cta: 'Lire le cas complet'
        },
        {
          title: 'Bibliothèque centrale de Tallinn',
          problem: 'Les processus manuels d\'enregistrement/prêt de livres étaient lents, entraînant de longues files d\'attente.',
          solution: 'Portails automatisés RFID et stations en libre-service installés pour 180 000 livres.',
          roi: '65% de traitement plus rapide, 40% de réduction de la charge de travail du personnel, 95% de satisfaction des utilisateurs',
          cta: 'Lire le cas complet'
        }
      ]
    },
    regional: {
      title: 'Présence locale dans les pays baltes',
      subtitle: 'Équipes d\'experts prêtes à vous servir',
      offices: [
        {
          city: 'Vilnius',
          country: 'Lituanie',
          description: 'Notre siège lituanien avec support technique et gestion de projets'
        },
        {
          city: 'Riga',
          country: 'Lettonie',
          description: 'Bureau à service complet desservant les municipalités et entreprises lettones'
        },
        {
          city: 'Tallinn',
          country: 'Estonie',
          description: 'Centre d\'opérations estonien pour livraison de projets locaux'
        }
      ]
    },
    contact: {
      title: 'Commencez aujourd\'hui',
      subtitle: 'Discutons de la façon dont l\'IoT peut transformer vos opérations',
      form: {
        fullName: 'Nom complet',
        company: 'Nom de l\'entreprise',
        email: 'Adresse e-mail',
        phone: 'Numéro de téléphone (optionnel)',
        country: 'Pays',
        projectType: 'Type de projet',
        numSites: 'Nombre de sites / actifs',
        urgency: 'Urgence du projet',
        message: 'Parlez-nous de vos besoins',
        submit: 'Commencer',
        countries: ['Lituanie', 'Lettonie', 'Estonie', 'Autre'],
        projectTypes: [
          'Comptage intelligent',
          'Suivi énergétique',
          'Suivi d\'actifs',
          'Qualité de l\'air intérieur',
          'Bibliothèques intelligentes',
          'Gestion de parking',
          'Autre'
        ],
        siteOptions: ['1-10', '11-50', '51-200', '200+'],
        urgencyOptions: ['Urgent (< 1 mois)', 'Bientôt (1-3 mois)', 'Planification (3-6 mois)', 'Futur (6+ mois)']
      }
    },
    blog: {
      title: 'Dernières actualités',
      subtitle: 'Tendances IoT, études de cas et actualités du secteur',
      readMore: 'Lire plus'
    },
    footer: {
      solutions: 'Solutions',
      company: 'Entreprise',
      legal: 'Légal',
      offices: 'Nos bureaux',
      lithuania: 'Lituanie',
      latvia: 'Lettonie',
      estonia: 'Estonie',
      rights: '© 2025 0g Baltics — Tous droits réservés.'
    }
  }
};

// Placeholder for LV, LT, EE - will add proper native translations
translations.lv = { ...translations.en };
translations.lt = { ...translations.en };
translations.ee = { ...translations.en };